import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from 'pages/guide/components/GuideInfoBox';
import GuidePageLayout from 'pages/guide/components/GuidePageLayout';
import GuideSection from 'pages/guide/components/GuideSection';
import GuideTable from 'pages/guide/components/GuideTable';

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
  <Box
    component="pre"
    sx={{
      bgcolor: 'action.hover',
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      overflow: 'auto',
      fontSize: '0.8rem',
      lineHeight: 1.6,
      fontFamily: 'monospace',
    }}
  >
    <code>{children}</code>
  </Box>
);

const GuideBackendDeploy: React.FC = () => {
  return (
    <GuidePageLayout
      title="バックエンド 手動デプロイ手順"
      description="バックエンドアプリケーション（Spring Boot）をAWS環境に手動でデプロイする手順です。"
      breadcrumbs={[{ label: 'デプロイ', path: '/guide/deploy/overview' }]}
    >
      <GuideInfoBox type="warning">
        本手順はDEVELOPER権限を持つユーザーのみ実行できます。認証情報は別途管理されているドキュメントを参照してください。
      </GuideInfoBox>

      <GuideSection title="前提条件">
        <Stack spacing={1.5}>
          <GuideTable
            headers={['項目', '要件']}
            rows={[
              ['AWS CLI', 'v2 がインストール済み'],
              ['Docker', 'インストール済み・デーモン起動中'],
              ['AWS認証', 'ap-northeast-1 リージョンで認証済み'],
              ['リポジトリ', 'ardito-inhouse-kanzhong-backend をローカルにクローン済み'],
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="アーキテクチャ">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            バックエンドは以下の構成で稼働しています。
          </Typography>
          <GuideTable
            headers={['コンポーネント', '役割']}
            rows={[
              ['ECR', 'Docker イメージレジストリ'],
              ['ECS (Fargate)', 'コンテナ実行環境'],
              ['ALB', 'ロードバランサー（api.kanzhong.app）'],
              ['RDS', 'PostgreSQL データベース'],
              ['Route 53', 'api.kanzhong.app のDNSレコード管理'],
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="手順">
        <Stack spacing={2.5}>
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 1: リポジトリを最新化
            </Typography>
            <CodeBlock>{`cd ardito-inhouse-kanzhong-backend
git checkout main
git pull origin main`}</CodeBlock>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 2: アプリケーションのビルド
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gradle でアプリケーションをビルドします。
            </Typography>
            <CodeBlock>{`./gradlew clean build -x test`}</CodeBlock>
            <GuideInfoBox>
              テストをスキップしています。事前に CI でテストが通っていることを確認してください。
            </GuideInfoBox>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 3: Docker イメージのビルド
            </Typography>
            <CodeBlock>{`docker build -t kanzhong-backend:latest .`}</CodeBlock>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 4: ECR にログイン
            </Typography>
            <CodeBlock>{`aws ecr get-login-password --region ap-northeast-1 | \\
  docker login --username AWS --password-stdin <AWS_ACCOUNT_ID>.dkr.ecr.ap-northeast-1.amazonaws.com`}</CodeBlock>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 5: Docker イメージのタグ付け・プッシュ
            </Typography>
            <CodeBlock>{`docker tag kanzhong-backend:latest \\
  <AWS_ACCOUNT_ID>.dkr.ecr.ap-northeast-1.amazonaws.com/<ECR_REPOSITORY>:latest

docker push \\
  <AWS_ACCOUNT_ID>.dkr.ecr.ap-northeast-1.amazonaws.com/<ECR_REPOSITORY>:latest`}</CodeBlock>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 6: ECS サービスの更新（新イメージでデプロイ）
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ECS サービスを強制的に新しいデプロイメントに更新します。
            </Typography>
            <CodeBlock>{`aws ecs update-service \\
  --cluster <ECS_CLUSTER_NAME> \\
  --service <ECS_SERVICE_NAME> \\
  --force-new-deployment \\
  --region ap-northeast-1`}</CodeBlock>
            <GuideInfoBox>
              ローリングアップデートにより、ダウンタイムなしでデプロイされます。新しいタスクが正常に起動してからold
              タスクが停止します。
            </GuideInfoBox>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 7: デプロイ完了の確認
            </Typography>
            <Typography variant="body2" color="text.secondary">
              新しいタスクが RUNNING 状態になるまで待機します（通常2〜5分）。
            </Typography>
            <CodeBlock>{`aws ecs wait services-stable \\
  --cluster <ECS_CLUSTER_NAME> \\
  --services <ECS_SERVICE_NAME> \\
  --region ap-northeast-1`}</CodeBlock>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Step 8: 動作確認
            </Typography>
            <CodeBlock>{`curl -I https://api.kanzhong.app/actuator/health`}</CodeBlock>
            <Typography variant="body2" color="text.secondary">
              HTTP 200 とステータス UP が返ることを確認します。
            </Typography>
          </Stack>
        </Stack>
      </GuideSection>

      <GuideSection title="自動デプロイ（GitHub Actions）">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            通常のデプロイは main ブランチへのプッシュで GitHub Actions
            が自動実行します。手動デプロイは緊急時やデバッグ時のみ使用してください。
          </Typography>
          <GuideTable
            headers={['トリガー', '処理内容']}
            rows={[
              [
                'main ブランチへのプッシュ',
                'Gradle build → Docker build → ECR push → ECS force-new-deployment',
              ],
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="ロールバック">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            デプロイ後に問題が発生した場合、前のイメージに戻すことでロールバックできます。
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              方法 1: 前のタスク定義リビジョンに戻す
            </Typography>
            <CodeBlock>{`# 現在のタスク定義を確認
aws ecs describe-services \\
  --cluster <ECS_CLUSTER_NAME> \\
  --services <ECS_SERVICE_NAME> \\
  --query 'services[0].taskDefinition' \\
  --output text \\
  --region ap-northeast-1

# 前のリビジョンでサービスを更新
aws ecs update-service \\
  --cluster <ECS_CLUSTER_NAME> \\
  --service <ECS_SERVICE_NAME> \\
  --task-definition <TASK_DEFINITION_ARN:前のリビジョン> \\
  --region ap-northeast-1`}</CodeBlock>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              方法 2: 前のコミットから再デプロイ
            </Typography>
            <CodeBlock>{`git checkout <前のコミットハッシュ>
# Step 2〜6 を再実行`}</CodeBlock>
          </Stack>
        </Stack>
      </GuideSection>

      <GuideSection title="トラブルシューティング">
        <Stack spacing={1.5}>
          <GuideTable
            headers={['症状', '原因', '対処']}
            rows={[
              [
                'ECS タスクが起動後すぐに停止する',
                'アプリケーションエラー（DB接続、設定不備等）',
                'CloudWatch Logs でコンテナログを確認',
              ],
              [
                'docker push で Access Denied',
                'ECR ログインが切れている',
                'Step 4 の ECR ログインを再実行',
              ],
              [
                'ヘルスチェックが失敗する',
                'アプリケーション起動に時間がかかっている',
                'ECS タスクのログを確認、ヘルスチェックの猶予期間を確認',
              ],
              [
                'DB マイグレーションエラー',
                'Flyway マイグレーションの競合',
                'RDS に接続してマイグレーション状態を確認',
              ],
            ]}
          />
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideBackendDeploy;
